import { useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../api";
import {
  assignCampaignRecipients,
  fetchAudienceCrmOptions,
  previewCampaignAudience,
  removeCampaignRecipients,
  type AudiencePreview,
  type CampaignRecipientPlan,
} from "./campaignBuilderApi";
import { SEGMENT_PRIMARY_VALUES } from "../constants";
import { SearchTile } from "../components/SearchTile";
import { deliverabilityLabel } from "./deliverability";
import {
  CampaignCompanyLink,
  CampaignContactLink,
  type CampaignCrmNavigation,
} from "./CrmRecordLinks";
import {
  countryFacetLabel,
  distinctCountryFacetLabels,
  hasUsCountry,
  sortCountryLabels,
} from "../../server/campaign-builder/countryCodes.ts";
import { attributionLabelsOnContacts, unionAttributionLabels } from "./attributionOptions";

export type JourneyStepOption = { step_id: string; name: string };

export type AudienceDraft = {
  channel: "email" | "sms";
  countries_in: string[];
  segments_in: string[];
  attributions_in: string[];
  locked: boolean;
};

function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function attributionIsSelected(selected: string[], label: string): boolean {
  const key = label.trim().toLowerCase();
  return selected.some((s) => s.trim().toLowerCase() === key);
}

function toggleAttributionLabel(selected: string[], label: string): string[] {
  const key = label.trim().toLowerCase();
  if (selected.some((s) => s.trim().toLowerCase() === key)) {
    return selected.filter((s) => s.trim().toLowerCase() !== key);
  }
  return [...selected, label];
}

function countryFacetKey(value: string): string {
  return (countryFacetLabel(value) ?? value).trim().toUpperCase();
}

function countryIsSelected(selected: string[], label: string): boolean {
  const key = countryFacetKey(label);
  return selected.some((c) => countryFacetKey(c) === key);
}

function toggleCountryLabel(selected: string[], label: string): string[] {
  const canonical = countryFacetLabel(label) ?? label.trim();
  const key = countryFacetKey(canonical);
  if (selected.some((c) => countryFacetKey(c) === key)) {
    return selected.filter((c) => countryFacetKey(c) !== key);
  }
  return [...selected, canonical];
}

function normalizeCountries(values: string[]): string[] {
  return distinctCountryFacetLabels(values);
}

/** Display order: US first, then alphabetical ISO codes. */
function sortCountriesForDisplay(countries: string[]): string[] {
  return sortCountryLabels(countries.map((country) => countryFacetLabel(country) ?? country));
}

function formatMatchCountry(contact: {
  match_country?: string | null;
  match_detail?: string | null;
  reasons: string[];
}): string {
  if (contact.match_country?.trim()) return contact.match_country.trim();
  for (const reason of contact.reasons) {
    if (reason.toLowerCase().startsWith("country ∈")) {
      return reason.replace(/^country\s*∈\s*/i, "").trim();
    }
  }
  const detail = contact.match_detail?.trim();
  if (detail && !detail.includes(",")) return detail;
  if (detail?.includes(",")) return detail.split(",")[0]?.trim() ?? "—";
  return "—";
}

function formatContactName(contact: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}): string {
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim();
  return name || contact.email?.trim() || "—";
}

function formatCompanyName(contact: {
  company?: string | null;
  account_name?: string | null;
}): string {
  return contact.company?.trim() || contact.account_name?.trim() || "—";
}

function contactMatchesListSearch(
  contact: {
    first_name?: string | null;
    last_name?: string | null;
    title?: string | null;
    email?: string | null;
    company?: string | null;
    account_name?: string | null;
    company_segment?: string | null;
    attributions?: string[] | null;
    match_detail?: string | null;
    reasons: string[];
  },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const parts = [
    formatContactName(contact),
    contact.title,
    contact.email,
    formatCompanyName(contact),
    formatCompanySegment(contact),
    formatContactAttributions(contact),
  ]
    .map((v) => (v && v !== "—" ? v.trim() : ""))
    .filter(Boolean);
  const haystack = parts.join(" ").toLowerCase();
  return haystack.includes(q);
}

function formatContactAttributions(contact: { attributions?: string[] | null }): string {
  const labels = contact.attributions?.filter(Boolean) ?? [];
  return labels.length ? labels.join(", ") : "—";
}

function formatCompanySegment(contact: {
  company_segment?: string | null;
  match_detail?: string | null;
  reasons: string[];
}): string {
  if (contact.company_segment?.trim()) return contact.company_segment.trim();
  const detail = contact.match_detail?.trim();
  if (detail?.includes(",")) {
    const rest = detail.split(",").slice(1).join(",").trim();
    if (rest) return rest;
  }
  for (const reason of contact.reasons) {
    if (reason.toLowerCase().startsWith("segment ∈")) {
      return reason.replace(/^segment\s*∈\s*/i, "").trim();
    }
  }
  return "—";
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function overallRulesForDraft(draft: AudienceDraft): string[] {
  if (draft.channel !== "email") return [];
  const rules = ["Has email address"];
  if (hasUsCountry(draft.countries_in)) {
    rules.push("Not unsubscribed (do not contact excluded)");
  }
  if (draft.attributions_in.length) {
    rules.push(`Contact attribution matches any of: ${draft.attributions_in.join(", ")}`);
  }
  return rules;
}

function draftToFilterJson(draft: AudienceDraft, campaignKey: string): Record<string, unknown> {
  return {
    version: 2,
    channel: draft.channel,
    campaign_key: campaignKey,
    countries_in: draft.countries_in,
    segments_in: draft.segments_in,
    attributions_in: draft.attributions_in,
    has_email: draft.channel === "email",
    locked: draft.locked,
  };
}

export function parseAudienceDraft(raw: Record<string, unknown> | null | undefined): AudienceDraft {
  if (!raw) {
    return {
      channel: "email",
      countries_in: ["US"],
      segments_in: [],
      attributions_in: [],
      locked: false,
    };
  }
  const channel = raw.channel === "sms" ? "sms" : "email";
  let countries_in = normalizeCountries(
    Array.isArray(raw.countries_in)
      ? raw.countries_in.map(String)
      : raw.country
        ? [String(raw.country)]
        : ["US"],
  );
  if (countries_in.length === 0) countries_in = ["US"];
  const segments_in = Array.isArray(raw.segments_in)
    ? raw.segments_in.map(String)
    : raw.segment
      ? [String(raw.segment)]
      : [];
  const attributions_in = Array.isArray(raw.attributions_in)
    ? raw.attributions_in.map(String)
    : raw.attribution
      ? [String(raw.attribution)]
      : [];
  return {
    channel,
    countries_in,
    segments_in,
    attributions_in,
    locked: raw.locked === true,
  };
}

export function AudienceBuilder({
  campaignId,
  campaignKey,
  initialFilter,
  journeySteps,
  recipientPlan,
  onAudienceChange,
  onRecipientPlanChange,
  crmNavigation,
}: {
  campaignId: string;
  campaignKey: string;
  initialFilter: Record<string, unknown> | null;
  journeySteps: JourneyStepOption[];
  recipientPlan: CampaignRecipientPlan | null;
  onAudienceChange?: (preview: AudiencePreview | null) => void;
  onRecipientPlanChange?: (plan: CampaignRecipientPlan) => void;
  crmNavigation?: CampaignCrmNavigation;
}) {
  const { accounts } = useMsal();
  const authAccountId = accounts[0]?.homeAccountId ?? "";
  const [crmOptions, setCrmOptions] = useState<{
    countries: string[];
    segments: string[];
    attributions: string[];
  } | null>(null);
  const [attributionCatalog, setAttributionCatalog] = useState<string[]>([]);
  const [attributionCatalogLoading, setAttributionCatalogLoading] = useState(true);
  const [crmOptionsError, setCrmOptionsError] = useState<string | null>(null);
  const [draft, setDraft] = useState<AudienceDraft>(() => parseAudienceDraft(initialFilter));
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [targetStepIds, setTargetStepIds] = useState<Set<string>>(() => new Set());
  const [assignBusy, setAssignBusy] = useState(false);
  const [listSearchQuery, setListSearchQuery] = useState("");
  const previewRequestId = useRef(0);
  const crmRequestId = useRef(0);
  const onAudienceChangeRef = useRef(onAudienceChange);
  onAudienceChangeRef.current = onAudienceChange;

  useEffect(() => {
    setDraft(parseAudienceDraft(initialFilter));
  }, [initialFilter, campaignId]);

  useEffect(() => {
    setSelectedIds(new Set());
    setTargetStepIds(new Set());
    setListSearchQuery("");
    setAttributionCatalog([]);
    setAttributionCatalogLoading(true);
  }, [campaignId]);

  const plannedByEmail = useMemo(() => {
    const map = new Map<string, CampaignRecipientPlan["recipients"][number]["assignments"]>();
    for (const row of recipientPlan?.recipients ?? []) {
      if (row.email) map.set(normalizeEmail(row.email), row.assignments ?? []);
    }
    return map;
  }, [recipientPlan]);

  const qualifiedRows = preview?.qualified ?? [];
  const filteredQualifiedRows = useMemo(() => {
    const q = listSearchQuery.trim();
    if (!q) return qualifiedRows;
    return qualifiedRows.filter((c) => contactMatchesListSearch(c, q));
  }, [qualifiedRows, listSearchQuery]);
  const allQualifiedSelected =
    filteredQualifiedRows.length > 0 &&
    filteredQualifiedRows.every((c) => selectedIds.has(c.contact_id));
  const someQualifiedSelected = qualifiedRows.some((c) => selectedIds.has(c.contact_id));
  const allJourneyStepsSelected =
    journeySteps.length > 0 && journeySteps.every((s) => targetStepIds.has(s.step_id));

  function toggleSelectAllQualified() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allQualifiedSelected) {
        for (const c of filteredQualifiedRows) next.delete(c.contact_id);
      } else {
        for (const c of filteredQualifiedRows) next.add(c.contact_id);
      }
      return next;
    });
  }

  function toggleRow(contactId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  }

  function toggleTargetStep(stepId: string) {
    setTargetStepIds((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  }

  function toggleAllTargetSteps() {
    if (allJourneyStepsSelected) {
      setTargetStepIds(new Set());
      return;
    }
    setTargetStepIds(new Set(journeySteps.map((s) => s.step_id)));
  }

  async function onAssignSelected() {
    if (!targetStepIds.size || !selectedIds.size) return;
    const contacts = qualifiedRows
      .filter((c) => selectedIds.has(c.contact_id) && c.email)
      .map((c) => ({
        contact_id: c.contact_id,
        email: String(c.email),
        first_name: c.first_name,
        last_name: c.last_name,
      }));
    if (!contacts.length) return;
    setAssignBusy(true);
    setError(null);
    try {
      const plan = await assignCampaignRecipients(campaignId, contacts, [...targetStepIds]);
      onRecipientPlanChange?.(plan);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not assign recipients");
    } finally {
      setAssignBusy(false);
    }
  }

  async function toggleContactOnEmailStep(
    contact: (typeof qualifiedRows)[number],
    stepId: string,
    include: boolean,
  ) {
    const email = contact.email?.trim();
    if (!email) return;
    setAssignBusy(true);
    setError(null);
    try {
      const plan = include
        ? await assignCampaignRecipients(
            campaignId,
            [
              {
                contact_id: contact.contact_id,
                email,
                first_name: contact.first_name,
                last_name: contact.last_name,
              },
            ],
            [stepId],
          )
        : await removeCampaignRecipients(campaignId, [email], [stepId]);
      onRecipientPlanChange?.(plan);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not update plan");
    } finally {
      setAssignBusy(false);
    }
  }

  async function onRemoveSelectedFromPlan() {
    const emails = qualifiedRows
      .filter((c) => selectedIds.has(c.contact_id) && c.email)
      .map((c) => String(c.email));
    if (!emails.length) return;
    setAssignBusy(true);
    setError(null);
    try {
      const stepIds = targetStepIds.size ? [...targetStepIds] : undefined;
      const plan = await removeCampaignRecipients(campaignId, emails, stepIds);
      onRecipientPlanChange?.(plan);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not remove recipients");
    } finally {
      setAssignBusy(false);
    }
  }

  const loadCrmOptions = useCallback(async () => {
    const requestId = ++crmRequestId.current;
    setAttributionCatalogLoading(true);
    try {
      const data = await fetchAudienceCrmOptions(campaignKey);
      if (requestId !== crmRequestId.current) return;
      setCrmOptions(data);
      setCrmOptionsError(null);
      if (data.attributions.length) {
        setAttributionCatalog((prev) => unionAttributionLabels(prev, data.attributions));
      }
    } catch (err: unknown) {
      if (requestId !== crmRequestId.current) return;
      if (err instanceof ApiError && err.status === 401) {
        setCrmOptionsError("Sign in to load country and attribution filters.");
        setAttributionCatalogLoading(false);
        return;
      }
      setCrmOptionsError(err instanceof Error ? err.message : "Could not load audience filters");
      setCrmOptions((prev) =>
        prev ?? {
          countries: [],
          segments: [...SEGMENT_PRIMARY_VALUES],
          attributions: [],
        },
      );
    }
    try {
      const scanned = await fetchAudienceCrmOptions(campaignKey, true);
      if (requestId !== crmRequestId.current) return;
      setAttributionCatalog((prev) => unionAttributionLabels(prev, scanned.attributions));
      setCrmOptions((prev) =>
        prev
          ? {
              ...prev,
              attributions: unionAttributionLabels(prev.attributions, scanned.attributions),
            }
          : scanned,
      );
    } catch {
      /* The checkbox list can still fill from an unfiltered preview. */
    } finally {
      if (requestId === crmRequestId.current) setAttributionCatalogLoading(false);
    }
  }, [campaignKey]);

  const filterJson = useMemo(() => draftToFilterJson(draft, campaignKey), [draft, campaignKey]);

  const countryOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const c of crmOptions?.countries ?? []) {
      const label = countryFacetLabel(c);
      if (!label) continue;
      const key = countryFacetKey(label);
      if (!byKey.has(key)) byKey.set(key, label);
    }
    for (const row of preview?.qualified ?? []) {
      const fromRow = row.match_country?.trim();
      const label = fromRow ? countryFacetLabel(fromRow) : null;
      if (!label) continue;
      const key = countryFacetKey(label);
      if (!byKey.has(key)) byKey.set(key, label);
    }
    if (!byKey.size) byKey.set("US", "US");
    return sortCountriesForDisplay([...byKey.values()]);
  }, [crmOptions, preview?.qualified]);

  const segmentOptions = useMemo(() => {
    if (crmOptions?.segments?.length) return crmOptions.segments;
    return [...SEGMENT_PRIMARY_VALUES];
  }, [crmOptions]);

  useEffect(() => {
    if (draft.attributions_in.length) return;
    const fromPreview = attributionLabelsOnContacts(preview?.qualified ?? []);
    if (!fromPreview.length) return;
    setAttributionCatalog((prev) => unionAttributionLabels(prev, fromPreview));
  }, [draft.attributions_in.length, preview]);

  const attributionOptions = useMemo(
    () => unionAttributionLabels(attributionCatalog, draft.attributions_in),
    [attributionCatalog, draft.attributions_in],
  );

  const runPreview = useCallback(async () => {
    if (draft.channel === "sms") {
      setPreview(null);
      onAudienceChangeRef.current?.(null);
      return;
    }
    const requestId = ++previewRequestId.current;
    setBusy(true);
    setError(null);
    try {
      const result = await previewCampaignAudience(filterJson);
      if (requestId !== previewRequestId.current) return;
      const merged = { ...result, filter: (result.filter ?? filterJson) as Record<string, unknown> };
      setPreview(merged);
      onAudienceChangeRef.current?.(merged);
    } catch (err: unknown) {
      if (requestId !== previewRequestId.current) return;
      setError(err instanceof Error ? err.message : "Audience preview failed");
    } finally {
      if (requestId === previewRequestId.current) setBusy(false);
    }
  }, [draft.channel, filterJson]);

  useEffect(() => {
    void loadCrmOptions();
  }, [loadCrmOptions, authAccountId]);

  useEffect(() => {
    const refreshAudienceData = () => {
      void loadCrmOptions();
      void runPreview();
    };
    window.addEventListener("ironfleet-auth-settled", refreshAudienceData);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void loadCrmOptions();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("ironfleet-auth-settled", refreshAudienceData);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadCrmOptions, runPreview]);

  useEffect(() => {
    if (draft.channel !== "email") {
      setPreview(null);
      onAudienceChangeRef.current?.(null);
      return;
    }
    const timer = window.setTimeout(() => {
      void runPreview();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [filterJson, draft.channel, runPreview]);

  const overallRules = overallRulesForDraft(draft);

  return (
    <div className="audience-builder">
      <div className="audience-builder-toolbar">
        <div className="audience-channel" role="radiogroup" aria-label="Channel type">
          <span className="audience-channel-label">Channel</span>
          <label>
            <input
              type="radio"
              name={`channel-${campaignId}`}
              checked={draft.channel === "email"}
              onChange={() => setDraft((d) => ({ ...d, channel: "email" }))}
            />
            Email
          </label>
          <label>
            <input
              type="radio"
              name={`channel-${campaignId}`}
              checked={draft.channel === "sms"}
              onChange={() => setDraft((d) => ({ ...d, channel: "sms" }))}
            />
            SMS
          </label>
        </div>
      </div>

      {error ? <p className="campaign-builder-error">{error}</p> : null}
      {crmOptionsError ? (
        <p className="campaign-builder-error" role="status">
          {crmOptionsError}{" "}
          <button type="button" className="ghost" onClick={() => void loadCrmOptions()}>
            Retry
          </button>
        </p>
      ) : null}

      {draft.channel === "sms" ? (
        <p className="hint audience-sms-placeholder">SMS audience targeting is coming soon.</p>
      ) : (
        <>
          <div className="audience-multi-grid audience-multi-grid-filters audience-multi-grid-filters-3">
            <div className="audience-multi-col">
              <h3>Country</h3>
              <div className="audience-multi-list audience-country-list" role="group" aria-label="Countries">
                {countryOptions.map((country) => (
                  <label key={country} className="audience-check-row">
                    <input
                      type="checkbox"
                      checked={countryIsSelected(draft.countries_in, country)}
                      onChange={() =>
                        setDraft((d) => ({ ...d, countries_in: toggleCountryLabel(d.countries_in, country) }))
                      }
                    />
                    {country}
                  </label>
                ))}
              </div>
            </div>
            <div className="audience-multi-col">
              <h3>Segment</h3>
              <div className="audience-multi-list audience-segment-list" role="group" aria-label="Segments">
                {segmentOptions.map((segment) => (
                  <label key={segment} className="audience-check-row">
                    <input
                      type="checkbox"
                      checked={draft.segments_in.includes(segment)}
                      onChange={() =>
                        setDraft((d) => ({ ...d, segments_in: toggleValue(d.segments_in, segment) }))
                      }
                    />
                    {segment}
                  </label>
                ))}
              </div>
            </div>
            <div className="audience-multi-col">
              <h3>Contact attribution</h3>
              <div
                className="audience-multi-list audience-attribution-list"
                role="group"
                aria-label="Contact attribution"
              >
                {attributionOptions.length ? (
                  attributionOptions.map((label) => (
                    <label key={label} className="audience-check-row">
                      <input
                        type="checkbox"
                        checked={attributionIsSelected(draft.attributions_in, label)}
                        onChange={() =>
                          setDraft((d) => ({
                            ...d,
                            attributions_in: toggleAttributionLabel(d.attributions_in, label),
                          }))
                        }
                      />
                      {label}
                    </label>
                  ))
                ) : attributionCatalogLoading ? (
                  <p className="hint audience-attribution-empty">Loading attribution labels…</p>
                ) : (
                  <p className="hint audience-attribution-empty">No attribution labels in CRM yet.</p>
                )}
              </div>
            </div>
          </div>

          {draft.channel === "email" && overallRules.length ? (
            <div className="audience-overall-rules">
              <h3>Overall rules</h3>
              <p className="hint audience-overall-rules-note">Rules may vary based on country selection.</p>
              <ul className="audience-hard-rules">
                {overallRules.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {preview ? (
            <>
              <div className="audience-stats">
                <span><strong>{preview.totals.qualified}</strong> qualified</span>
                <span><strong>{preview.totals.suppressed}</strong> suppressed</span>
                <span><strong>{preview.totals.evaluated}</strong> evaluated</span>
                {recipientPlan?.recipients.length ? (
                  <span><strong>{recipientPlan.recipients.length}</strong> planned in campaign</span>
                ) : null}
                {busy ? <span className="hint audience-stats-updating">Updating…</span> : null}
              </div>

              {journeySteps.length ? (
                <div className="audience-recipient-actions">
                  <div className="audience-recipient-actions-head">
                    <h3>Add to campaign emails</h3>
                    <p className="hint">
                      Select contacts below, choose one or more emails, then assign. Unique by email address.
                    </p>
                  </div>
                  <div className="audience-step-picks" role="group" aria-label="Campaign emails">
                    <label className="audience-check-row audience-step-pick-all">
                      <input
                        type="checkbox"
                        checked={allJourneyStepsSelected}
                        disabled={assignBusy}
                        onChange={() => toggleAllTargetSteps()}
                      />
                      All emails
                    </label>
                    {journeySteps.map((step) => (
                      <label key={step.step_id} className="audience-check-row">
                        <input
                          type="checkbox"
                          checked={targetStepIds.has(step.step_id)}
                          disabled={assignBusy}
                          onChange={() => toggleTargetStep(step.step_id)}
                        />
                        <span className="journey-email-icon" aria-hidden="true">✉</span>
                        {step.name}
                      </label>
                    ))}
                  </div>
                  <div className="audience-recipient-buttons">
                    <button
                      type="button"
                      className="secondary"
                      disabled={assignBusy || !someQualifiedSelected}
                      onClick={() => setSelectedIds(new Set())}
                    >
                      Clear selection
                    </button>
                    <button
                      type="button"
                      disabled={assignBusy || !someQualifiedSelected || !targetStepIds.size}
                      onClick={() => void onAssignSelected()}
                    >
                      {assignBusy ? "Saving…" : "Add selected to emails"}
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      disabled={assignBusy || !someQualifiedSelected}
                      onClick={() => void onRemoveSelectedFromPlan()}
                    >
                      Remove selected
                    </button>
                  </div>
                </div>
              ) : (
                <p className="hint audience-recipient-hint">Add journey emails below to assign contacts to specific templates.</p>
              )}

              <div className="audience-contact-search">
                <SearchTile
                  value={listSearchQuery}
                  onChange={setListSearchQuery}
                  hintId={`audience-list-search-hint-${campaignId}`}
                  hintLabel="Filter contacts in the audience list"
                  fields={["Name", "Email", "Title", "Company", "Country", "Company segment", "Attribution"]}
                  placeholder="Search contacts…"
                />
                {listSearchQuery.trim() ? (
                  <p className="hint audience-contact-search-meta">
                    Showing {filteredQualifiedRows.length} of {qualifiedRows.length} contacts
                  </p>
                ) : null}
              </div>

              <div className="audience-table-wrap">
                <table className="rating-table campaign-audience-table">
                  <colgroup>
                    <col className="audience-col-select" />
                    <col className="audience-col-name" />
                    <col className="audience-col-title" />
                    <col className="audience-col-company" />
                    <col className="audience-col-email" />
                    <col className="audience-col-country" />
                    <col className="audience-col-segment" />
                    <col className="audience-col-attribution" />
                    <col className="audience-col-planned" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="audience-col-select">
                        <input
                          type="checkbox"
                          aria-label="Select all contacts in list"
                          checked={allQualifiedSelected}
                          disabled={!filteredQualifiedRows.length}
                          onChange={() => toggleSelectAllQualified()}
                        />
                      </th>
                      <th className="audience-col-name">Name</th>
                      <th className="audience-col-title">Title</th>
                      <th className="audience-col-company">Company</th>
                      <th className="audience-col-email">Email</th>
                      <th className="audience-col-country">Country</th>
                      <th className="audience-col-segment">Company segment</th>
                      <th className="audience-col-attribution">Attribution</th>
                      <th className="audience-col-planned">Planned emails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQualifiedRows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="hint audience-table-empty">
                          {listSearchQuery.trim()
                            ? "No contacts match this search."
                            : "No qualified contacts."}
                        </td>
                      </tr>
                    ) : null}
                    {filteredQualifiedRows.map((c) => {
                      const planned = c.email ? plannedByEmail.get(normalizeEmail(String(c.email))) : undefined;
                      return (
                        <tr key={c.contact_id} className={selectedIds.has(c.contact_id) ? "is-selected" : undefined}>
                          <td className="audience-col-select">
                            <input
                              type="checkbox"
                              aria-label={`Select ${[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email}`}
                              checked={selectedIds.has(c.contact_id)}
                              onChange={() => toggleRow(c.contact_id)}
                            />
                          </td>
                          <td className="audience-col-name">
                            <CampaignContactLink
                              contactId={c.contact_id}
                              label={formatContactName(c)}
                              navigation={crmNavigation}
                            />
                          </td>
                          <td className="hint audience-col-title">{c.title?.trim() || "—"}</td>
                          <td className="hint audience-col-company">
                            <CampaignCompanyLink
                              accountId={c.account_id}
                              label={formatCompanyName(c)}
                              navigation={crmNavigation}
                            />
                          </td>
                          <td className="audience-col-email">{c.email ?? "—"}</td>
                          <td className="hint audience-col-country">{formatMatchCountry(c)}</td>
                          <td className="hint audience-col-segment">{formatCompanySegment(c)}</td>
                          <td className="hint audience-col-attribution">{formatContactAttributions(c)}</td>
                          <td className="hint audience-planned-cell audience-col-planned">
                            {journeySteps.length ? (
                              <div className="audience-planned-step-checks" role="group" aria-label="Planned emails">
                                {journeySteps.map((step) => {
                                  const assignment = planned?.find((a) => a.step_id === step.step_id);
                                  const status = assignment?.deliverability_status;
                                  const checked = Boolean(assignment);
                                  const canToggle = !assignment || status === "planned";
                                  const contactLabel =
                                    [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "contact";
                                  return (
                                    <label key={step.step_id} className="audience-planned-step-check">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={assignBusy || !c.email || !canToggle}
                                        title={
                                          canToggle
                                            ? undefined
                                            : `${deliverabilityLabel(status ?? "planned")} — cannot remove here`
                                        }
                                        aria-label={`${checked ? "Remove" : "Add"} ${contactLabel} on ${step.name}`}
                                        onChange={(e) =>
                                          void toggleContactOnEmailStep(c, step.step_id, e.target.checked)
                                        }
                                      />
                                      <span className="audience-planned-step-name">{step.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : busy ? (
            <p className="hint">Running audience search…</p>
          ) : null}
        </>
      )}
    </div>
  );
}
